// ====================================================================
// TOUCH + TAP-TO-MOVE CONTROLS
// ====================================================================
// The design plan's K–2 input commitment: young kids struggle with two-handed
// WASD, and a large share of school Chromebooks are touch/2-in-1 devices.
//
// Two layers, deliberately separate:
//   - Tap/click-to-move: works for EVERYONE (mouse, trackpad, touch). Tap the
//     ground and Chocky walks there; a soft ring marks the goal.
//   - Virtual joystick + jump button: overlay shown only on touch devices.
//
// This module never moves the player itself. It writes plain state into
// `touchInput` and updatePlayer() in main.js consumes it exactly like the
// keyboard — same acceleration, same camera-relative math, same jump buffer.
// All UI is created here with inline styles (no markup/CSS edits, no
// backdrop-filter — see the low-power rules in index.html).
import * as THREE from 'three';

export const touchInput = {
  // Joystick deflection in WASD screen semantics: x right +, y down + (so
  // pushing the stick up gives y = -1, same as the W key's iz -= 1).
  stickX: 0,
  stickY: 0,
  stickMag: 0,          // 0..1 deflection; > RUN_DEFLECT means run
  jumpHeld: false,
  moveTarget: null,     // THREE.Vector3 | null — tap-to-move goal on y=0
  targetRun: false,     // latched when a tap target is created; does not drop to walk near the end
  targetAge: 0,         // seconds since the target was set (grace for accel)
  blockedTime: 0,       // accumulated "trying but not moving" — cancels target
};

export const RUN_DEFLECT = 0.9;

// maxTouchPoints catches touch-capable 2-in-1s even when a mouse is also
// present; those kids get both input styles.
export const HAS_TOUCH = (navigator.maxTouchPoints || 0) > 0 || 'ontouchstart' in window;

const TAP_MAX_PX = 14;       // finger wobble allowance
const TAP_MAX_MS = 400;
const TARGET_MAX_DIST = 30;  // clamp taps at the foggy horizon to a sane walk

let marker = null;
let markerT = 0;
let ctx = null;
let stickBase = null, jumpBtn = null;
let stickNub = null;
const REDUCED_MOTION = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const JUMP_BG = 'rgba(255,255,255,0.3)';

function resetTouchHardwareState() {
  touchInput.stickX = 0; touchInput.stickY = 0; touchInput.stickMag = 0;
  touchInput.jumpHeld = false;
  if (stickNub) stickNub.style.transform = '';
  if (jumpBtn) jumpBtn.style.background = JUMP_BG;
}

export function initTouchControls(context) {
  ctx = context;   // { scene, camera, canvas, terrainMesh, heightAt, getPlayerPos, isActive }

  initTapToMove();
  if (HAS_TOUCH) {
    buildJoystick();
    buildJumpButton();
    adaptHelpForTouch();
  }

  // Pointer capture is lost silently when the tab hides or loses focus —
  // never leave a phantom stick deflection or held jump behind.
  const reset = () => resetTouchHardwareState();
  addEventListener('blur', reset);
  document.addEventListener('visibilitychange', () => { if (document.hidden) reset(); });
}

// Show/hide the overlay with play mode (main.js calls this from setPlayMode).
export function setTouchUIVisible(on) {
  if (stickBase) stickBase.style.display = on ? 'block' : 'none';
  if (jumpBtn) jumpBtn.style.display = on ? 'flex' : 'none';
  if (!on) clearMoveTarget();
}

export function clearMoveTarget() {
  touchInput.moveTarget = null;
  touchInput.targetRun = false;
  touchInput.targetAge = 0;
  touchInput.blockedTime = 0;
  if (marker) marker.visible = false;
}

// Called from the main loop while in play mode: pulse the goal ring.
export function updateTouchMarker(dt) {
  if (!marker) return;
  if (!touchInput.moveTarget) { marker.visible = false; return; }
  markerT += dt;
  marker.visible = true;
  marker.position.set(touchInput.moveTarget.x, touchInput.moveTarget.y + 0.03, touchInput.moveTarget.z);
  const s = REDUCED_MOTION ? 1 : 1 + Math.sin(markerT * 6) * 0.15;
  marker.scale.setScalar(s);
}

// ---------- tap / click to move ----------
const _ndc = new THREE.Vector2();
const _ray = new THREE.Raycaster();
const _groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
const _hit = new THREE.Vector3();

function initTapToMove() {
  const canvas = ctx.canvas;
  let downX = 0, downY = 0, downT = 0, downId = -1;

  canvas.addEventListener('pointerdown', (e) => {
    if (!e.isPrimary) return;
    downX = e.clientX; downY = e.clientY; downT = performance.now(); downId = e.pointerId;
  });

  canvas.addEventListener('pointerup', (e) => {
    if (e.pointerId !== downId || !ctx.isActive()) return;
    downId = -1;
    // A tap, not a drag (drags belong to free-orbit camera / scroll gestures).
    if (performance.now() - downT > TAP_MAX_MS) return;
    if (Math.hypot(e.clientX - downX, e.clientY - downY) > TAP_MAX_PX) return;

    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    _ndc.set(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1
    );
    _ray.setFromCamera(_ndc, ctx.camera);
    // Raycast walkable obstacle tops first (platforms/roofs), then the real
    // terrain (hills included); the flat y=0 plane is only the fallback. One
    // pointer-up raycast path, so this stays cheap.
    let hitOk = false;
    if (ctx.raycastObstacleTops) {
      hitOk = ctx.raycastObstacleTops(
        _ray.ray.origin.x, _ray.ray.origin.y, _ray.ray.origin.z,
        _ray.ray.direction.x, _ray.ray.direction.y, _ray.ray.direction.z,
        TARGET_MAX_DIST * 4, _hit
      );
    }
    if (ctx.terrainMesh) {
      const hits = _ray.intersectObject(ctx.terrainMesh, false);
      if (hits.length && (!hitOk || hits[0].distance < _ray.ray.origin.distanceTo(_hit))) {
        _hit.copy(hits[0].point); hitOk = true;
      }
    }
    if (!hitOk && !_ray.ray.intersectPlane(_groundPlane, _hit)) return;   // tapped the sky

    // Near-horizon rays intersect absurdly far away; walk that direction, but
    // only a sane distance from where Chocky stands now.
    const p = ctx.getPlayerPos();
    const dx = _hit.x - p.x, dz = _hit.z - p.z;
    const d = Math.hypot(dx, dz);
    if (d < 0.3) return;
    if (d > TARGET_MAX_DIST) {
      _hit.x = p.x + (dx / d) * TARGET_MAX_DIST;
      _hit.z = p.z + (dz / d) * TARGET_MAX_DIST;
      if (ctx.heightAt) _hit.y = ctx.heightAt(_hit.x, _hit.z);
    }
    touchInput.moveTarget = touchInput.moveTarget || new THREE.Vector3();
    touchInput.moveTarget.set(_hit.x, _hit.y, _hit.z);
    touchInput.targetRun = d > 6;
    touchInput.targetAge = 0;
    touchInput.blockedTime = 0;
    markerT = 0;
    ensureMarker();
  });

  canvas.addEventListener('pointercancel', () => { downId = -1; });
}

function ensureMarker() {
  if (marker) return;
  marker = new THREE.Mesh(
    new THREE.RingGeometry(0.22, 0.34, 24),
    new THREE.MeshBasicMaterial({ color: 0xff6b6b, transparent: true, opacity: 0.75, depthWrite: false })
  );
  marker.rotation.x = -Math.PI / 2;
  marker.renderOrder = 2;
  ctx.scene.add(marker);
}

// ---------- virtual joystick (touch only) ----------
function buildJoystick() {
  const SIZE = 124, NUB = 52;
  stickBase = document.createElement('div');
  stickBase.id = 'touchStick';
  stickBase.style.cssText = `
    position:fixed; left:18px; bottom:78px; width:${SIZE}px; height:${SIZE}px;
    border-radius:50%; background:rgba(255,255,255,0.22);
    border:2px solid rgba(255,255,255,0.45); z-index:8;
    touch-action:none; user-select:none; -webkit-user-select:none;
    display:none;`;
  const nub = document.createElement('div');
  stickNub = nub;
  nub.style.cssText = `
    position:absolute; left:${(SIZE - NUB) / 2}px; top:${(SIZE - NUB) / 2}px;
    width:${NUB}px; height:${NUB}px; border-radius:50%;
    background:rgba(255,255,255,0.85); box-shadow:0 2px 8px rgba(0,0,0,0.25);
    pointer-events:none;`;
  stickBase.appendChild(nub);
  document.body.appendChild(stickBase);

  const R = SIZE / 2;
  let activeId = -1;

  const setFromEvent = (e) => {
    const rect = stickBase.getBoundingClientRect();
    let dx = e.clientX - (rect.left + R);
    let dy = e.clientY - (rect.top + R);
    const len = Math.hypot(dx, dy);
    const max = R - NUB / 4;
    if (len > max) { dx *= max / len; dy *= max / len; }
    nub.style.transform = `translate(${dx}px, ${dy}px)`;
    const mag = Math.min(1, len / max);
    if (mag < 0.18) {           // dead zone
      touchInput.stickX = 0; touchInput.stickY = 0; touchInput.stickMag = 0;
    } else {
      touchInput.stickX = dx / max;
      touchInput.stickY = dy / max;     // screen down = +, matches WASD's S
      touchInput.stickMag = mag;
    }
  };
  const release = () => {
    activeId = -1;
    nub.style.transform = '';
    touchInput.stickX = 0; touchInput.stickY = 0; touchInput.stickMag = 0;
  };

  stickBase.addEventListener('pointerdown', (e) => {
    activeId = e.pointerId;
    stickBase.setPointerCapture(e.pointerId);
    clearMoveTarget();          // direct steering overrides tap-to-move
    setFromEvent(e);
  });
  stickBase.addEventListener('pointermove', (e) => { if (e.pointerId === activeId) setFromEvent(e); });
  stickBase.addEventListener('pointerup', (e) => { if (e.pointerId === activeId) release(); });
  stickBase.addEventListener('pointercancel', release);
  stickBase.addEventListener('lostpointercapture', release);
}

// ---------- jump button (touch only) ----------
function buildJumpButton() {
  jumpBtn = document.createElement('div');
  jumpBtn.id = 'touchJump';
  jumpBtn.textContent = '⬆';
  jumpBtn.style.cssText = `
    position:fixed; right:22px; bottom:88px; width:84px; height:84px;
    border-radius:50%; background:rgba(255,255,255,0.3);
    border:2px solid rgba(255,255,255,0.55); z-index:8;
    display:none; align-items:center; justify-content:center;
    font:800 34px/1 ui-sans-serif, system-ui, sans-serif; color:rgba(27,31,42,0.7);
    touch-action:none; user-select:none; -webkit-user-select:none;`;
  document.body.appendChild(jumpBtn);

  // Hold to jump higher — identical semantics to holding Space.
  jumpBtn.addEventListener('pointerdown', (e) => {
    jumpBtn.setPointerCapture(e.pointerId);
    touchInput.jumpHeld = true;
    jumpBtn.style.background = 'rgba(255,107,107,0.5)';
  });
  const off = () => {
    touchInput.jumpHeld = false;
    jumpBtn.style.background = JUMP_BG;
  };
  jumpBtn.addEventListener('pointerup', off);
  jumpBtn.addEventListener('pointercancel', off);
  jumpBtn.addEventListener('lostpointercapture', off);
}

// ---------- touch-appropriate help text ----------
function adaptHelpForTouch() {
  // setTouchUIVisible() flexes the jump button; make its hidden state stick.
  // (display:none → block in setTouchUIVisible; the button uses flex when shown)
  const panel = document.querySelector('#playHelp .play-help-panel');
  if (panel) {
    panel.innerHTML = `
      <div class="play-help-row">
        <span class="play-help-label">Move</span>
        <span class="play-help-items"><span>drag the stick</span><span>or tap the ground</span></span>
      </div>
      <div class="play-help-row">
        <span class="play-help-label">Jump</span>
        <span class="play-help-items"><span>hold <kbd>⬆</kbd> — longer = higher</span></span>
      </div>`;
  }
  const hutHint = document.getElementById('gameDialogHint');
  if (hutHint) hutHint.innerHTML = 'Tap this card to play &nbsp;·&nbsp; walk away to cancel';
}
